import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { studySubjectHidden, studyToolHidden, useStudyAvailability } from './studyAvailability';
import { studyToolRoute } from './studyToolRoute';
import { toolsHubPath } from './studyTools';

/**
 * SIR-CTL-02: a deep link to a tool that the learner's Institution or class turned off shows a
 * notice instead of the tool. The server also refuses the tool's state reads and writes; this
 * gate only stops the page from loading. Unknown (loading, failed or family learner) shows the
 * tool, because only an explicit server "off" hides it.
 */
export function StudyToolRouteGate({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const { pathname } = useLocation();
  const route = studyToolRoute(pathname);
  const availability = useStudyAvailability(enabled && route !== null);
  if (!enabled || !route) return <>{children}</>;
  const subjectOff = studySubjectHidden(availability.data, route.subjectKey);
  const toolOff = studyToolHidden(availability.data, route.toolId);
  if (!subjectOff && !toolOff) return <>{children}</>;
  return (
    <section
      role="status"
      data-testid="study-tool-turned-off"
      className="mx-auto mt-8 max-w-xl rounded-3xl bg-wash-sunshine p-6 text-center"
    >
      <h1 className="text-xl font-bold text-ink">
        {subjectOff ? 'Your school has turned this subject off' : 'Your school has turned this tool off'}
      </h1>
      <p className="mt-2 text-sm text-slate2">Ask your teacher if you think you need it.</p>
      <Link
        to={subjectOff ? '/learn/study/subjects' : toolsHubPath(route.subjectKey)}
        className="btn-pill-secondary mt-4 inline-block"
      >
        {subjectOff ? 'Back to subjects' : 'Back to tools'}
      </Link>
    </section>
  );
}
